export interface StoredGame {
  id: string;
  source: "lichess" | "chesscom" | "manual" | "pgn";
  pgn: string;
  white: string;
  black: string;
  result: string;
  opening: string | null;
  eco: string | null;
  playedAt: string | null;
  importedAt: string;
  accuracyWhite: number | null;
  accuracyBlack: number | null;
  blundersWhite: number;
  blundersBlack: number;
  playerColor: "white" | "black" | null;
}

export interface StoredAttempt {
  id: string;
  puzzleId: string;
  solved: boolean;
  accuracy: number | null;
  durationMs: number;
  createdAt: string;
}

export interface StoredDrill {
  id: string;
  gameId: string;
  ply: number;
  fen: string;
  bestMove: string;
  category: string;
  loss: number;
  side: "white" | "black";
  createdAt: string;
}

export interface StoredSettings {
  puzzleRating: number;
  streak: number;
  bestStreak: number;
  sessionsCompleted: number;
  puzzlesSolved: number;
  puzzlesAttempted: number;
  lastTrainerAt: string | null;
  lichessUsername: string | null;
  chesscomUsername: string | null;
}

export const DEFAULT_SETTINGS: StoredSettings = {
  puzzleRating: 1200,
  streak: 0,
  bestStreak: 0,
  sessionsCompleted: 0,
  puzzlesSolved: 0,
  puzzlesAttempted: 0,
  lastTrainerAt: null,
  lichessUsername: null,
  chesscomUsername: null,
};

export const STORAGE_DB_NAME = "onchainreps";
export const STORAGE_DB_VERSION = 2;

export const STORES = {
  games: "games",
  srs: "srs",
  attempts: "attempts",
  settings: "settings",
  drills: "drills",
} as const;

export function openStorageDb(
  factory: IDBFactory = globalThis.indexedDB,
  name = STORAGE_DB_NAME,
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = factory.open(name, STORAGE_DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORES.games)) {
        const store = db.createObjectStore(STORES.games, { keyPath: "id" });
        store.createIndex("importedAt", "importedAt");
        store.createIndex("source", "source");
      }
      if (!db.objectStoreNames.contains(STORES.srs)) {
        db.createObjectStore(STORES.srs, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORES.attempts)) {
        const store = db.createObjectStore(STORES.attempts, { keyPath: "id" });
        store.createIndex("puzzleId", "puzzleId");
        store.createIndex("createdAt", "createdAt");
      }
      if (!db.objectStoreNames.contains(STORES.settings)) {
        db.createObjectStore(STORES.settings);
      }
      if (!db.objectStoreNames.contains(STORES.drills)) {
        const store = db.createObjectStore(STORES.drills, { keyPath: "id" });
        store.createIndex("gameId", "gameId");
        store.createIndex("createdAt", "createdAt");
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Could not open local storage"));
    request.onblocked = () => reject(new Error("Local storage upgrade blocked by another tab"));
  });
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Storage request failed"));
  });
}

export class LocalStore {
  private constructor(private readonly db: IDBDatabase) {}

  static async open(factory?: IDBFactory): Promise<LocalStore> {
    const db = await openStorageDb(factory);
    return new LocalStore(db);
  }

  async listGames(): Promise<StoredGame[]> {
    const tx = this.db.transaction(STORES.games, "readonly");
    const rows = await requestToPromise<StoredGame[]>(
      tx.objectStore(STORES.games).getAll() as IDBRequest<StoredGame[]>,
    );
    return rows.sort((a, b) => b.importedAt.localeCompare(a.importedAt));
  }

  async getGame(id: string): Promise<StoredGame | undefined> {
    const tx = this.db.transaction(STORES.games, "readonly");
    return requestToPromise<StoredGame | undefined>(
      tx.objectStore(STORES.games).get(id) as IDBRequest<StoredGame | undefined>,
    );
  }

  async saveGames(games: StoredGame[]): Promise<void> {
    if (games.length === 0) return;
    const tx = this.db.transaction(STORES.games, "readwrite");
    const store = tx.objectStore(STORES.games);
    for (const game of games) store.put(game);
    await transactionDone(tx);
  }

  async deleteGame(id: string): Promise<void> {
    const tx = this.db.transaction(STORES.games, "readwrite");
    tx.objectStore(STORES.games).delete(id);
    await transactionDone(tx);
  }

  async getSrsItems<T>(prefix?: string): Promise<T[]> {
    const tx = this.db.transaction(STORES.srs, "readonly");
    const rows = await requestToPromise<T[]>(
      tx.objectStore(STORES.srs).getAll() as IDBRequest<T[]>,
    );
    if (!prefix) return rows;
    return rows.filter((row) => String((row as { id?: string }).id ?? "").startsWith(prefix));
  }

  async putSrsItem(item: unknown): Promise<void> {
    const tx = this.db.transaction(STORES.srs, "readwrite");
    tx.objectStore(STORES.srs).put(item);
    await transactionDone(tx);
  }

  async recordAttempt(attempt: StoredAttempt): Promise<void> {
    const tx = this.db.transaction(STORES.attempts, "readwrite");
    tx.objectStore(STORES.attempts).put(attempt);
    await transactionDone(tx);
  }

  async listAttempts(): Promise<StoredAttempt[]> {
    const tx = this.db.transaction(STORES.attempts, "readonly");
    return requestToPromise<StoredAttempt[]>(
      tx.objectStore(STORES.attempts).getAll() as IDBRequest<StoredAttempt[]>,
    );
  }

  async listDrills(): Promise<StoredDrill[]> {
    const tx = this.db.transaction(STORES.drills, "readonly");
    const rows = await requestToPromise<StoredDrill[]>(
      tx.objectStore(STORES.drills).getAll() as IDBRequest<StoredDrill[]>,
    );
    return rows.sort((a, b) => b.loss - a.loss);
  }

  async saveDrills(drills: StoredDrill[]): Promise<void> {
    if (drills.length === 0) return;
    const tx = this.db.transaction(STORES.drills, "readwrite");
    const store = tx.objectStore(STORES.drills);
    for (const drill of drills) store.put(drill);
    await transactionDone(tx);
  }

  async deleteDrillsForGame(gameId: string): Promise<void> {
    const tx = this.db.transaction(STORES.drills, "readwrite");
    const index = tx.objectStore(STORES.drills).index("gameId");
    const keys = await requestToPromise<IDBValidKey[]>(
      index.getAllKeys(gameId) as IDBRequest<IDBValidKey[]>,
    );
    const store = tx.objectStore(STORES.drills);
    for (const key of keys) store.delete(key);
    await transactionDone(tx);
  }

  async getSettings(): Promise<StoredSettings> {
    const tx = this.db.transaction(STORES.settings, "readonly");
    const stored = await requestToPromise<StoredSettings | undefined>(
      tx.objectStore(STORES.settings).get("settings") as IDBRequest<StoredSettings | undefined>,
    );
    return { ...DEFAULT_SETTINGS, ...(stored ?? {}) };
  }

  async putSettings(settings: StoredSettings): Promise<void> {
    const tx = this.db.transaction(STORES.settings, "readwrite");
    tx.objectStore(STORES.settings).put(settings, "settings");
    await transactionDone(tx);
  }

  close(): void {
    this.db.close();
  }
}

function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("Storage transaction failed"));
    tx.onabort = () => reject(tx.error ?? new Error("Storage transaction aborted"));
  });
}
